import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, CITIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Search, Bug, Check, Zap, Clock, CalendarDays, HardHat, Wind, Wrench, Hammer, Home, Building2, FlaskConical, Droplets, Layers, Waves, ShieldCheck, Calculator, Triangle } from "lucide-react";
import { serviceLabel } from "@/lib/api";

const SERVICE_META = {
  home_inspection: { desc: "Pre-purchase, roof, sewer, mold & more", icon: Search },
  pest_control: { desc: "Termite, rodents, ants, bed bugs & more", icon: Bug },
  roofing: { desc: "Repair, replacement, leaks & gutters", icon: HardHat },
  hvac: { desc: "AC, heating, furnace & duct service", icon: Wind },
  general_contractor: { desc: "Remodels, additions, repairs & more", icon: Hammer },
  residential_inspection: { desc: "Buyer, seller & pre-listing home checks", icon: Home },
  commercial_inspection: { desc: "Offices, retail, multi-family & industrial", icon: Building2 },
  termite_pest_inspection: { desc: "Termite reports & wood-destroying pests", icon: Bug },
  roof_inspection: { desc: "Roof condition, leaks & certifications", icon: Triangle },
  mold_environmental: { desc: "Mold, air quality, radon & asbestos", icon: FlaskConical },
  sewer_septic: { desc: "Sewer scopes & septic system checks", icon: Droplets },
  foundation_structural: { desc: "Cracks, settling & structural reports", icon: Layers },
  pool_spa: { desc: "Pool, spa, pumps & safety checks", icon: Waves },
  electrical_hvac_inspection: { desc: "Panels, wiring, AC & furnace checks", icon: Zap },
  insurance_inspection: { desc: "4-point, roof & underwriting inspections", icon: ShieldCheck },
  appraisal: { desc: "Home value for sales, refis & estates", icon: Calculator },
};
const buildService = (c) => ({
  key: c.key,
  label: c.label,
  desc: SERVICE_META[c.key]?.desc || `Local ${c.label} services & quotes`,
  icon: SERVICE_META[c.key]?.icon || Wrench,
});
const DEFAULT_SERVICES = [
  { key: "home_inspection", label: "Home Inspection" },
  { key: "pest_control", label: "Pest Control" },
  { key: "roofing", label: "Roofing" },
  { key: "hvac", label: "HVAC" },
  { key: "general_contractor", label: "General Contractor" },
].map(buildService);
const urgencies = [
  { key: "asap", label: "ASAP", icon: Zap },
  { key: "soon", label: "Within a week", icon: Clock },
  { key: "flexible", label: "Flexible", icon: CalendarDays },
];

export const QuoteWizard = ({ onDone, presetService, presetCity }) => {
  const [step, setStep] = useState(presetService ? 1 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({
    service_type: presetService || "", city: presetCity || "", zip: "", property_type: "",
    urgency: "flexible", details: "", name: "", email: "", phone: "", address: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [services, setServices] = useState(DEFAULT_SERVICES);
  useEffect(() => {
    api.get("/categories").then(({ data }) => setServices(data.map(buildService))).catch(() => {});
  }, []);

  const canNext = () => {
    if (step === 0) return !!f.service_type;
    if (step === 1) return f.city && f.zip;
    if (step === 2) return true;
    if (step === 3) return f.name && f.email && f.phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", f);
      setDone(data);
      toast.success(data.matched_count > 0 ? "Request sent! Local pros have been notified." : "Request received! We'll connect you with a local pro soon.");
      onDone && onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="quote-success">
        <div className="w-16 h-16 rounded-full btn-primary grid place-items-center mx-auto mb-5">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-display text-2xl font-bold text-[color:var(--navy)]">You're all set!</h3>
        <p className="text-[color:var(--ink-2)] mt-2 max-w-md mx-auto">
          {done.matched_count > 0
            ? `${done.matched_count} verified local ${serviceLabel(f.service_type).toLowerCase()} pros in the Sacramento area were matched to your request. Expect responses shortly.`
            : "We've received your request and emailed you a confirmation. We're lining up local pros for your area and will connect you as soon as one is available."}
        </p>
      </div>
    );
  }

  const progress = ((step + 1) / 4) * 100;

  return (
    <div data-testid="quote-wizard">
      <div className="step-bar mb-2"><div style={{ width: `${progress}%` }} /></div>
      <p className="text-xs font-mono text-[color:var(--ink-3)] mb-6">STEP {step + 1} OF 4</p>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
          {step === 0 && (
            <div>
              <h3 className="font-display text-2xl font-bold text-[color:var(--navy)] mb-1">What service do you need?</h3>
              <p className="text-[color:var(--ink-2)] mb-6">Choose the type of pro you're looking for.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((s) => (
                  <button key={s.key} onClick={() => set("service_type", s.key)}
                    data-testid={`quote-service-${s.key.replaceAll("_", "-")}-card`}
                    className={`card-soft p-6 text-left ${f.service_type === s.key ? "!border-[color:var(--emerald)] ring-2 ring-emerald-100" : ""}`}>
                    <s.icon className="w-8 h-8 text-[color:var(--emerald)] mb-3" />
                    <div className="font-display font-bold text-lg text-[color:var(--navy)]">{s.label}</div>
                    <div className="text-sm text-[color:var(--ink-2)] mt-1">{s.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h3 className="font-display text-2xl font-bold text-[color:var(--navy)] mb-1">Where's the property?</h3>
              <p className="text-[color:var(--ink-2)] mb-6">We serve Sacramento County + 60-mile radius.</p>
              <div className="space-y-4">
                <div>
                  <Label>City</Label>
                  <select value={f.city} onChange={(e) => set("city", e.target.value)}
                    data-testid="quote-city-select"
                    className="w-full h-11 rounded-lg border border-slate-300 px-3 mt-1 bg-white">
                    <option value="">Select a city</option>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value="Other">Other (nearby)</option>
                  </select>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>ZIP Code</Label><Input value={f.zip} onChange={(e) => set("zip", e.target.value)} placeholder="95814" data-testid="quote-zip-input" className="mt-1" /></div>
                  <div><Label>Street Address (optional)</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St" data-testid="quote-address-input" className="mt-1" /></div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="font-display text-2xl font-bold text-[color:var(--navy)] mb-1">Tell us the details</h3>
              <p className="text-[color:var(--ink-2)] mb-6">The more we know, the better your matches.</p>
              <div className="space-y-4">
                <div><Label>Property Type</Label><Input value={f.property_type} onChange={(e) => set("property_type", e.target.value)} placeholder="Single family, condo, townhouse..." data-testid="quote-property-input" className="mt-1" /></div>
                <div>
                  <Label className="mb-2 block">How soon?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {urgencies.map((u) => (
                      <button key={u.key} onClick={() => set("urgency", u.key)} data-testid={`quote-urgency-${u.key}`}
                        className={`card-soft p-3 flex flex-col items-center gap-1 ${f.urgency === u.key ? "!border-[color:var(--emerald)] ring-2 ring-emerald-100" : ""}`}>
                        <u.icon className="w-5 h-5 text-[color:var(--emerald)]" />
                        <span className="text-xs font-medium">{u.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div><Label>Describe what you need</Label><Textarea value={f.details} onChange={(e) => set("details", e.target.value)} rows={3} placeholder="e.g. Full inspection for a 3bd/2ba built in 1998" data-testid="quote-details-input" className="mt-1" /></div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="font-display text-2xl font-bold text-[color:var(--navy)] mb-1">How can pros reach you?</h3>
              <p className="text-[color:var(--ink-2)] mb-6">Your contact is only shared with matched, verified pros.</p>
              <div className="space-y-4">
                <div><Label>Full Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" data-testid="quote-name-input" className="mt-1" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" data-testid="quote-email-input" className="mt-1" /></div>
                  <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="916-555-0100" data-testid="quote-phone-input" className="mt-1" /></div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-8">
        <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="quote-back-button" className="rounded-full">Back</Button>
        {step < 3 ? (
          <Button disabled={!canNext()} onClick={() => setStep((s) => s + 1)} data-testid={`quote-wizard-step-${step + 1}-next`} className="btn-primary rounded-full px-8 font-semibold">Continue</Button>
        ) : (
          <Button disabled={!canNext() || submitting} onClick={submit} data-testid="quote-submit-button" className="btn-amber rounded-full px-8 font-semibold">{submitting ? "Sending..." : "Get My Free Quotes"}</Button>
        )}
      </div>
    </div>
  );
};
